/**
 * Maths for the image cropper. Everything is in the image's own (natural) pixels, so the crop survives the
 * frame being resized: the view is a zoom plus the image point that sits in the frame's centre.
 */

export type CropKind = 'avatar' | 'icon' | 'banner';

export interface CropShape {
  /** Frame width / height. */
  aspect: number;
  /** Output width at most; the height follows the aspect. Never upscaled past the picked area. */
  outputWidth: number;
  /** CSS border-radius of the frame, matching how the image is shown. */
  radius: string;
}

// Profile banners show at 300×100 (popouts), 448×128 (full profile) and 280×96 (settings preview); space banners
// at 288×128 (channel list header) and 300×88 (tag card). 3:1 matches the profile spots and loses little elsewhere.
export const CROP_SHAPES: Record<CropKind, CropShape> = {
  avatar: { aspect: 1, outputWidth: 512, radius: '50%' },
  icon: { aspect: 1, outputWidth: 512, radius: '30%' },
  banner: { aspect: 3, outputWidth: 1500, radius: '0.75rem' },
};

export const MIN_ZOOM = 1;
export const MAX_ZOOM = 5;

export interface Size {
  width: number;
  height: number;
}

export interface CropView {
  zoom: number;
  /** Image point (natural px) under the frame's centre. */
  cx: number;
  cy: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const clampZoom = (zoom: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));

/** The image area the frame covers at zoom 1: the largest rect of the frame's aspect that fits the image. */
export function baseCropSize(image: Size, aspect: number): Size {
  return image.width / image.height > aspect ? { width: image.height * aspect, height: image.height } : { width: image.width, height: image.width / aspect };
}

export function initialView(image: Size): CropView {
  return { zoom: 1, cx: image.width / 2, cy: image.height / 2 };
}

/** Clamps zoom and keeps the frame inside the image. */
export function clampView(image: Size, aspect: number, view: CropView): CropView {
  const zoom = clampZoom(view.zoom);
  const base = baseCropSize(image, aspect);
  const halfW = base.width / zoom / 2;
  const halfH = base.height / zoom / 2;
  return {
    zoom,
    cx: Math.min(image.width - halfW, Math.max(halfW, view.cx)),
    cy: Math.min(image.height - halfH, Math.max(halfH, view.cy)),
  };
}

/** The picked area in natural pixels. */
export function cropRect(image: Size, aspect: number, view: CropView): Rect {
  const v = clampView(image, aspect, view);
  const base = baseCropSize(image, aspect);
  const width = base.width / v.zoom;
  const height = base.height / v.zoom;
  return { x: v.cx - width / 2, y: v.cy - height / 2, width, height };
}

/** Moves the image by a drag of (dx, dy) screen pixels on a frame `frameWidth` pixels wide. */
export function panBy(image: Size, aspect: number, view: CropView, dx: number, dy: number, frameWidth: number): CropView {
  const perPx = baseCropSize(image, aspect).width / view.zoom / frameWidth;
  return clampView(image, aspect, { zoom: view.zoom, cx: view.cx - dx * perPx, cy: view.cy - dy * perPx });
}

/**
 * Zooms to `zoom`, keeping the image point under `focus` still. `focus` is relative to the frame's centre as a
 * fraction of its width and height (so (0, 0) is the centre and (0.5, 0.5) the bottom right corner).
 */
export function zoomTo(image: Size, aspect: number, view: CropView, zoom: number, focus = { x: 0, y: 0 }): CropView {
  const base = baseCropSize(image, aspect);
  const next = clampZoom(zoom);
  const px = view.cx + focus.x * (base.width / view.zoom);
  const py = view.cy + focus.y * (base.height / view.zoom);
  return clampView(image, aspect, { zoom: next, cx: px - focus.x * (base.width / next), cy: py - focus.y * (base.height / next) });
}

/** Output canvas size for a picked area: the shape's width, or the area's own width if that is smaller. */
export function outputSize(rect: Rect, shape: CropShape): Size {
  const width = Math.max(1, Math.round(Math.min(shape.outputWidth, rect.width)));
  return { width, height: Math.max(1, Math.round(width / shape.aspect)) };
}

/**
 * Whether the bytes are an animated image, which a canvas crop would freeze: any GIF, animated WebP (VP8X
 * animation flag), APNG (acTL before IDAT) and AVIF image sequences ('avis' brand).
 */
export function isAnimatedImage(bytes: Uint8Array): boolean {
  const ascii = (at: number, len: number) => String.fromCharCode(...bytes.subarray(at, at + len));
  if (ascii(0, 3) === 'GIF') return true;
  if (ascii(0, 4) === 'RIFF' && ascii(8, 4) === 'WEBP') return ascii(12, 4) === 'VP8X' && ((bytes[20] ?? 0) & 0x02) !== 0;
  if (bytes[0] === 0x89 && ascii(1, 3) === 'PNG') {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    for (let at = 8; at + 8 <= bytes.length; ) {
      const type = ascii(at + 4, 4);
      if (type === 'acTL') return true;
      if (type === 'IDAT') return false;
      at += 12 + view.getUint32(at);
    }
    return false;
  }
  if (ascii(4, 4) === 'ftyp') {
    const size = Math.min(bytes.length, new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(0));
    for (let at = 8; at + 4 <= size; at += 4) if (ascii(at, 4) === 'avis') return true;
  }
  return false;
}
